import { Buffer } from "buffer";
import type { AccountInfo, PublicKey } from "@solana/web3.js";
import type { TokenMetadataView } from "./types";
import { readEmbeddedTokenMetadata } from "./token2022";

const metadataCache = new Map<string, Promise<TokenMetadataView>>();

function safeRemoteUrl(value?: string): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("ipfs://")) {
    return `https://ipfs.io/ipfs/${value.slice("ipfs://".length)}`;
  }
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

export function safeGithubUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase();
    if (url.protocol !== "https:" || (hostname !== "github.com" && hostname !== "www.github.com")) {
      return undefined;
    }
    url.hostname = "github.com";
    return url.toString();
  } catch {
    return undefined;
  }
}

function parseOnchainMetadata(
  mint: PublicKey,
  account: AccountInfo<Buffer>,
): TokenMetadataView {
  const metadata = readEmbeddedTokenMetadata(account);
  if (!metadata) {
    return {
      name: `Token ${mint.toBase58().slice(0, 4)}`,
      symbol: mint.toBase58().slice(0, 5).toUpperCase(),
    };
  }
  return {
    name: metadata.name.trim() || `Token ${mint.toBase58().slice(0, 4)}`,
    symbol: metadata.symbol.trim() || mint.toBase58().slice(0, 5).toUpperCase(),
    uri: safeRemoteUrl(metadata.uri.trim()),
  };
}

async function hydrateFromJson(base: TokenMetadataView): Promise<TokenMetadataView> {
  if (!base.uri) return base;
  try {
    const response = await fetch(base.uri, {
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(6_000),
    });
    if (!response.ok) return base;
    const json = (await response.json()) as Record<string, unknown>;
    return {
      ...base,
      description:
        typeof json.description === "string" ? json.description.slice(0, 500) : undefined,
      image: safeRemoteUrl(typeof json.image === "string" ? json.image : undefined),
      github: safeGithubUrl(typeof json.website === "string" ? json.website : undefined),
    };
  } catch {
    return base;
  }
}

export function loadTokenMetadata(
  mint: PublicKey,
  account: AccountInfo<Buffer>,
): Promise<TokenMetadataView> {
  const key = mint.toBase58();
  const cached = metadataCache.get(key);
  if (cached) return cached;
  const promise = hydrateFromJson(parseOnchainMetadata(mint, account));
  metadataCache.set(key, promise);
  return promise;
}
